/**
 * LocaleProvider — single source of truth for the browser-side language
 * state. It:
 *   • Watches the URL for /ar prefix and syncs `i18n.language` accordingly.
 *   • Sets `<html dir>` and `<html lang>` on every language change.
 *   • Persists the active language in localStorage (`djspy_lang`).
 *   • Emits canonical + hreflang tags for BILINGUAL public routes so
 *     Google understands EN ↔ AR as language alternatives.  Covered
 *     families (all have Arabic equivalents at /ar<same-path>):
 *       - `/` and `/ar/`
 *       - `/music` and `/ar/music`
 *       - `/music/premium` and `/ar/music/premium`
 *       - `/music/free` and `/ar/music/free`
 *     For each locale variant we emit:
 *       - <link rel="canonical" href="{self URL}">
 *       - <link rel="alternate" hreflang="en" href="{EN URL}">
 *       - <link rel="alternate" hreflang="ar" href="{AR URL}">
 *       - <link rel="alternate" hreflang="x-default" href="{EN URL}">
 *       - <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1">
 *   • For every OTHER /ar/* route (i.e. AR shell where an Arabic
 *     equivalent has not been audited yet), keeps the safe fallback:
 *       - <meta name="robots" content="noindex,follow">
 *       - <link rel="canonical" href="{English source URL}">
 *       - no hreflang alternates emitted (nothing to advertise yet).
 *   • Pages that manage their own bilingual SEO (TrackPage, BlogPost,
 *     MediaCenter) are recognised via SELF_MANAGED_SEO_PATTERNS and
 *     left untouched.
 *   • Exposes `useLocale()` returning `{ lang, isRTL, setLang }`. `setLang`
 *     navigates the user to the localised equivalent of the current path.
 *
 * NOTE: URL is the source-of-truth. Storage is only used to remember the
 * choice for a returning visitor landing on "/" — direct hits to /ar/*
 * always override storage.
 */
import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { STORAGE_KEY, AR_LOCALE_ENABLED } from "@/i18n";
import { langFromPath, stripAr, localisedUrl } from "@/lib/i18nRoutes";

// Routes that inject their own canonical / robots / hreflang via their
// own `useEffect`. LocaleProvider must NOT stomp on those.
const SELF_MANAGED_SEO_PATTERNS = [
  /^\/(?:ar\/)?track\/[^/]+\/?$/,   // TrackPage
  /^\/(?:ar\/)?blog\/[^/]+\/?$/,    // BlogPost
  /^\/(?:ar\/)?media\/?$/,          // MediaCenter
  /^\/(?:ar\/)?arabic-remixes\/?$/, // Arabic Remixes SEO hub
  /^\/(?:ar\/)?blog\/?$/,          // Blog listing
  /^\/(?:ar\/)?music\/albums\/[^/]+\/?$/, // AlbumDetail
];

// Public bilingual routes with confirmed AR equivalents.  Match EN
// paths only; the AR variant is `${prefix}${path}`.
const BILINGUAL_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/music\/?$/,
  /^\/music\/latest\/?$/,
  /^\/music\/originals\/?$/,
  /^\/music\/premium\/?$/,
  /^\/music\/free\/?$/,
  /^\/music\/edits\/?$/,
  /^\/genre\/[^/]+\/?$/,
  /^\/(?:privacy|terms|refund)\/?$/,
];

// Account, auth, checkout and admin routes: never indexable, self canonical.
const NOINDEX_ROUTE_PATTERNS = [
  /^\/(?:ar\/)?(?:login|signup|forgot-password|reset-password|verify-email|library|account|cart|payment-success)\/?$/,
  /^\/(?:ar\/)?checkout\/(?:success|cancel)\/?$/,
  /^\/admin(?:\/.*)?$/,
];

const PRODUCTION_ORIGIN = "https://djspyofficial.com";

function setMetaRobots(content) {
  let el = document.querySelector('meta[name="robots"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "robots");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function clearHreflangs() {
  document
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((n) => n.remove());
}

function setHreflang(hreflang, href) {
  const el = document.createElement("link");
  el.setAttribute("rel", "alternate");
  el.setAttribute("hreflang", hreflang);
  el.setAttribute("href", href);
  document.head.appendChild(el);
}

/**
 * Emit the full canonical + hreflang set for a bilingual public
 * route.  Both English and Arabic variants are advertised.
 */
function emitBilingualSEO(currentLang, enPath) {
  const cleanEn = enPath.replace(/\/+$/, "") || "/";
  const enUrl = `${PRODUCTION_ORIGIN}${cleanEn === "/" ? "/" : cleanEn}`;
  const arUrl = `${PRODUCTION_ORIGIN}/ar${cleanEn === "/" ? "/" : cleanEn}`;
  const selfUrl = currentLang === "ar" ? arUrl : enUrl;

  setMetaRobots("index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
  setCanonical(selfUrl);
  clearHreflangs();
  setHreflang("en", enUrl);
  setHreflang("ar", arUrl);
  setHreflang("x-default", enUrl);
}

// Generic public/index.html head (keep in sync). Constants, not a DOM snapshot,
// because the first-loaded page may be a prerendered shell with its own head.
const BASELINE_HEAD = {
  title: "DJ SPY Official — Arabic Remixes, DJ Edits Download & DJ Booking Dubai",
  description: "Official DJ SPY digital music store — buy Arabic remixes, DJ edits, Khaleeji and Iraqi remix packs, and premium DJ mashups with instant full-quality download. Professional DJ booking in Dubai and across the GCC. Secure Stripe checkout.",
  robots: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  canonical: `${PRODUCTION_ORIGIN}/`,
  hreflangs: [["en", `${PRODUCTION_ORIGIN}/`], ["x-default", `${PRODUCTION_ORIGIN}/`]],
};

// While a 404 page is mounted the provider must not re-emit canonical/robots.
export const seoOverride = { notFound: false };

export function applyNotFoundHead(title) {
  setMetaRobots("noindex,nofollow");
  document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
  clearHreflangs();
  document.title = title;
}

export function setMetaDescription(content) {
  let el = document.querySelector('meta[name="description"]');
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", "description");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function restoreBaselineHead() {
  document.title = BASELINE_HEAD.title;
  setMetaDescription(BASELINE_HEAD.description);
  setMetaRobots(BASELINE_HEAD.robots);
  setCanonical(BASELINE_HEAD.canonical);
  clearHreflangs();
  BASELINE_HEAD.hreflangs.forEach(([l, h]) => setHreflang(l, h));
}

const LocaleContext = createContext({
  lang: "en",
  isRTL: false,
  arEnabled: AR_LOCALE_ENABLED,
  setLang: () => {},
});

export function LocaleProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = AR_LOCALE_ENABLED ? langFromPath(location.pathname) : "en";
  const isRTL = lang === "ar";

  // Sync i18next + <html> attributes whenever the URL-derived language changes.
  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    if (typeof document !== "undefined") {
      const html = document.documentElement;
      html.setAttribute("lang", lang);
      html.setAttribute("dir", isRTL ? "rtl" : "ltr");
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch { /* ignore quota / private mode */ }
  }, [lang, isRTL, i18n]);

  // Canonical + hreflang emitter.  Runs on every route change; parent
  // effect fires AFTER child page effects so this is authoritative.
  useEffect(() => {
    const path = location.pathname || "/";
    if (seoOverride.notFound) return;
    // Pages that manage their own bilingual SEO — leave alone.
    if (SELF_MANAGED_SEO_PATTERNS.some((r) => r.test(path))) return;

    if (NOINDEX_ROUTE_PATTERNS.some((r) => r.test(path))) {
      setMetaRobots("noindex,follow");
      setCanonical(`${PRODUCTION_ORIGIN}${path.replace(/\/+$/, "") || "/"}`);
      clearHreflangs();
      return;
    }

    const enPath = stripAr(path) || "/";
    const isBilingual = BILINGUAL_ROUTE_PATTERNS.some((r) => r.test(enPath));

    if (isBilingual) {
      emitBilingualSEO(lang, enPath);
      return;
    }

    // Un-audited AR shell — keep safe fallback so it never competes
    // with the English canonical version for search.
    if (isRTL) {
      setMetaRobots("noindex,follow");
      setCanonical(`${PRODUCTION_ORIGIN}${enPath}`);
      clearHreflangs();
    }
    // English un-audited routes: no-op; the page itself (if any)
    // sets its own canonical via its own useEffect.
  }, [lang, isRTL, location.pathname]);

  const setLang = (next) => {
    if (!AR_LOCALE_ENABLED && next === "ar") return;
    if (next === lang) return;
    navigate(localisedUrl(window.location, next), { replace: false });
  };

  const value = useMemo(
    () => ({ lang, isRTL, arEnabled: AR_LOCALE_ENABLED, setLang }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, isRTL],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
